import { IBaseAlertProps } from "@components/alert/Alert";
import { IGetValueArgs, TGetValueFn } from "@components/smart/FieldInfo";
import { IEditableAmountDialogData } from "@components/smart/selectionTableView/EditableAmountDialog";
import {
    editableAmountLinkFormatter,
    ISelectionWithEditableAmountCustomTableData
} from "@components/smart/selectionTableView/SelectionTableView.utils";
import { ISmartODataTableAPI, TSmartODataTableStorage } from "@components/smart/smartTable/SmartODataTableBase";
import { TFormatterFn } from "@components/smart/smartTable/SmartTable.utils";
import { ITabData } from "@components/tabs";
import BindingContext from "@odata/BindingContext";
import { isODataError } from "@odata/Data.types";
import {
    DocumentEntity,
    EntitySetName,
    IDocumentEntity,
    IPrPayrollPeriodEntity,
    IPrPayslipDeductionEntity,
    IPrPayslipEmploymentEntity,
    IPrPayslipEntity,
    PrAttendanceEntity,
    PrPayrollPeriodDocumentEntity,
    PrPayrollPeriodEntity,
    PrPayslipEmploymentEntity,
    PrPayslipEntity
} from "@odata/GeneratedEntityTypes";
import {
    PrAttendanceStatusCode,
    PrDeductionTypeCode,
    PrPayrollPeriodStatusCode,
    PrPayslipOverrideStatusCode
} from "@odata/GeneratedEnums";
import { OData } from "@odata/OData";
import { getSalaryCurrencyFactory } from "@pages/payroll/employee/Employee.utils";
import { IFormTableListRow, IFormTableListSection } from "@pages/payroll/payslip/formTableList/FormTableList";
import { TabsStyled } from "@pages/payroll/payslip/Payslip.styles";
import { roundToDecimalPlaces } from "@utils/general";
import { logger } from "@utils/log";
import memoizeOne from "@utils/memoizeOne";
import i18next from "i18next";
import React, { ReactElement } from "react";

import { BackendErrorCode, Status } from "../../../enums";
import { isEvalaProduction } from "../../../global.utils";
import { ITableStorageDefaultCustomData, TableStorage } from "../../../model/TableStorage";
import CurrencyType from "../../../types/Currency";
import { currencyScaleFormatter } from "../../../types/Number";
import { FormStorage } from "../../../views/formView/FormStorage";

export interface IPayslipCustomData extends ITableStorageDefaultCustomData {
    payrollPeriods?: IPrPayrollPeriodEntity[];
    selectedPeriod?: IPrPayrollPeriodEntity;
}

export enum DialogType {
    SalaryComponent = "SalaryComponent",
    ExtraSalary = "ExtraSalary",
    Receivables = "Receivables",
    Liabilities = "Liabilities",
    PayslipManualChange = "PayslipManualChange",
}

/**
 *  *** Employment Tabs ***
 **/
export const currentEmploymentPath = BindingContext.localContext("currentEmployment");
export const tabsPath = BindingContext.localContext("employmentTabs");

export function getPayslipEmploymentTabId(employment: IPrPayslipEmploymentEntity): string {
    return employment?.Id?.toString();
}

export const isPayslipEmploymentVisible = ({ bindingContext, storage }: IGetValueArgs): boolean =>
        bindingContext.getKey()?.toString() === storage.getValueByPath(currentEmploymentPath);

export function setCurrentPayslipEmploymentTabId(storage: FormStorage<IPrPayslipEntity>, tabId: string): void {
    storage.setValueByPath(currentEmploymentPath, tabId);
}

export const getEmploymentName = (empl: IPrPayslipEmploymentEntity) =>
        empl ? i18next.t(`Payslip:Form.EmpType_${empl.Employment.TypeCode}`, { number: empl.Employment?.NumberOurs } ) : "-";

export const renderPayslipEmploymentTabs = memoizeOne((employments: IPrPayslipEmploymentEntity[], storage: FormStorage<IPrPayslipEntity>): ReactElement => {
    const data = employments?.map((pe): ITabData => ({
        id: getPayslipEmploymentTabId(pe),
        title: getEmploymentName(pe),
    }));

    return (
            <TabsStyled data={data}
                        onChange={setCurrentPayslipEmploymentTabId.bind(null, storage)}
                        selectedTabId={storage.getValueByPath(currentEmploymentPath)}/>
    );
}, (employments, storage) => [employments, storage.getValueByPath(currentEmploymentPath)]);

export async function fetchPayrollPeriods(storage: TableStorage<ISmartODataTableAPI, IPayslipCustomData>): Promise<void> {
    // fetch payroll periods
    const query = storage.oData.getEntitySetWrapper(EntitySetName.PrPayrollPeriods).query()
            .select(PrPayrollPeriodEntity.Id, PrPayrollPeriodEntity.Month, PrPayrollPeriodEntity.Year, PrPayrollPeriodEntity.StatusCode)
            .orderBy(PrPayrollPeriodEntity.Year)
            .orderBy(PrPayrollPeriodEntity.Month);

    if (!isEvalaProduction()) {
        query.expand(PrPayrollPeriodEntity.Documents, (q) =>
                q.select(PrPayrollPeriodDocumentEntity.PeriodDocumentType).expand(PrPayrollPeriodDocumentEntity.Document, (q2) =>
                        q2.select(DocumentEntity.Id, DocumentEntity.NumberOurs, DocumentEntity.DocumentTypeCode)));
    }

    const res = await query.fetchData<IPrPayrollPeriodEntity[]>();
    const payrollPeriods = res.value;

    storage.setCustomData({
        payrollPeriods
    });
}

export function getPayslipPeriodFilter(period: IPrPayrollPeriodEntity): string {
    return `${PrPayslipEntity.Month} eq ${period.Month} and ${PrPayslipEntity.Year} eq ${period.Year}`;
}

export async function fetchUnfinishedPayslipsCount(oData: OData, period: IPrPayrollPeriodEntity): Promise<number> {
    const periodFilter = getPayslipPeriodFilter(period);
    const anyConditionFilter = [
            `${PrPayslipEntity.Employments}/any(e: e/${PrPayslipEmploymentEntity.Attendance}/${PrAttendanceEntity.StatusCode} eq '${PrAttendanceStatusCode.ToProcess}')`,
            `${PrPayslipEntity.OverrideStatusCode} eq '${PrPayslipOverrideStatusCode.Removed}'`
    ].join(" OR ");

    const query = oData.getEntitySetWrapper(EntitySetName.PrPayslips).query()
            .select(PrPayrollPeriodEntity.Id, PrPayrollPeriodEntity.Month, PrPayrollPeriodEntity.Year)
            .filter(`${periodFilter} AND (${anyConditionFilter})`)
            .count();
    try {
        const res = await query.fetchData<IPrPayslipEntity>();
        return res._metadata.count;
    } catch(e) {
        logger.error("Error fetching unfinished payslips count", e);
    }
    return 0;
}

export async function setPayrollPeriodStatus(storage: TableStorage<ISmartODataTableAPI, IPayslipCustomData>, period: IPrPayrollPeriodEntity, status: PrPayrollPeriodStatusCode): Promise<true | IBaseAlertProps> {
    let alert: IBaseAlertProps;
    try {
        await storage.oData.getEntitySetWrapper(EntitySetName.PrPayrollPeriods).update(period.Id, { StatusCode: status });
        period.StatusCode = status;
        if (period.Status) {
            period.Status.Code = status;
        }
        return true;
    } catch (e) {
        let errors: string[];
        if (isODataError(e)) {
            if (e._code === BackendErrorCode.ValidationError) {
                const errMap = new Set<string>();
                e._validationMessages?.forEach((m) =>
                        errMap.add(m.message));
                errors = Array.from(errMap);
            } else {
                errors = [e._message];
            }
        }
        alert = {
            status: Status.Error,
            title: i18next.t("Common:Errors.ErrorHappened"),
            subTitle: errors ?? i18next.t("Common:Errors.InternalError"),
        };
    }
    return alert;
}

export const dayFormatter = (value: number): string => `${value} d`;
export const fullDayFormatter = (value: number): string => i18next.t("Common:General.XDays", { count: value });
export const hoursFormatter = (value: number): string => `${value} h`;
export const minutesToHoursFormatter = (value: number): string => hoursFormatter(roundToDecimalPlaces(0, value / 60 ));

export const AttendanceOverviewProps = [
    { id: PrPayslipEmploymentEntity.PlannedWork },
    { id: PrPayslipEmploymentEntity.Worked },
    { id: PrPayslipEmploymentEntity.HolidayWorked },
    { id: PrPayslipEmploymentEntity.NightWorked },
    { id: PrPayslipEmploymentEntity.OvertimeWorked },
    { id: PrPayslipEmploymentEntity.WeekendWorked },
];

export function getAttendanceOverviewSections(storage: FormStorage<IPrPayslipEntity>, bc: BindingContext): IFormTableListSection[] {
    const payslipEmployment = storage.getValue(bc) as IPrPayslipEmploymentEntity;

    const worked = payslipEmployment[PrPayslipEmploymentEntity.Worked];
    const notWorked = -1 * Math.min(payslipEmployment.Attendance.Balance, 0);
    const overtime = Math.max(payslipEmployment.Attendance.Balance, 0);
    // todo: take correct item: payslipEmployment.Items.find(item => item.TypeCode === PrPayslipEmploymentItemTypeCode.WorkedDays);
    const workedDays = roundToDecimalPlaces(0, worked / 60 / 8);
    return [{
        rows: [
            { title: i18next.t("Payslip:Form.AttendanceWorkedDays"), value: workedDays, formatter: dayFormatter },
            { title: i18next.t("Payslip:Form.AttendanceWorked"), value: worked, formatter: minutesToHoursFormatter },
            notWorked && { title: i18next.t("Payslip:Form.AttendanceNotWorked"), value: notWorked, formatter: minutesToHoursFormatter },
            overtime && { title: i18next.t("Payslip:Form.OvertimeWorked"), value: overtime, formatter: minutesToHoursFormatter },
        ].filter(Boolean) as IFormTableListRow[] // filter out undefined values
    }];
}

export const getPayslipCurrency = getSalaryCurrencyFactory(PrPayslipEntity.Employee);

export const payslipCurrencyScaleFormatter: TFormatterFn = (value, args) => {
    const currency = args?.storage && getPayslipCurrency(args);
    return currencyScaleFormatter(value as number, currency);
};

export const payslipCurrencyUnit: TGetValueFn<string> = (args) => {
    return CurrencyType.getCurrencyUnit(getPayslipCurrency(args)?.Code);
};

export function isCustomPayslipDeduction(item: IPrPayslipDeductionEntity): boolean {
    return item.Deduction.TypeCode === PrDeductionTypeCode.Custom;
}

export const payslipPairDocumentAmountFormatter: TFormatterFn = (value, args) => {
    return editableAmountLinkFormatter(args);
};

export const getEditableAmountDialogData = (storage: TSmartODataTableStorage<ISelectionWithEditableAmountCustomTableData>, item: IDocumentEntity, rowId: string): IEditableAmountDialogData => {
    const { pairedItems } = storage.getCustomData();
    const pairedItem = pairedItems[rowId];
    return {
        Amount: (pairedItem?.Amount ?? 0) * item.ExchangeRatePerUnit,
        Currency: item.Currency,
        ExchangeRatePerUnit: item.ExchangeRatePerUnit,
        TotalAmount: item.TransactionAmount,
        TransactionAmount: pairedItem?.TransactionAmount,
        TransactionCurrency: item.TransactionCurrency,
        NumberOurs: item.NumberOurs,
    };
};
